import React from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, ArrowDownRight } from "lucide-react";
import { fadeUpAnimated, fadeIn, tagsAnimated } from "@/lib/motion-presets";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/8ef309bd-1e0c-4c8b-b104-8f04d73819f4/images/afdeb7033a4d1c8a407fe9e6b54d11d7cd209de0c39e0f2d59d3837ec824de3c.png";

const Hero = () => {
  const goMenu = () => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative min-h-[100vh] w-full overflow-hidden pt-24" data-testid="hero-section">
      <img src={HERO_BG} alt="West African feast" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "saturate(1.1) contrast(1.08)" }} />
      <div className="absolute inset-0 hero-grad" />

      {/* Floating tags */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 pt-10">
        <motion.div
          {...tagsAnimated}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 flex-wrap"
        >
          <span className="tag-pill" data-testid="hero-tag-soft-launch">
            <span className="pulse-dot inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--awd-ochre)" }} />
            Soft Launch · Pre-Order Only
          </span>
          <span className="tag-pill">Limited Plates</span>
          <span className="tag-pill">Made Fresh</span>
        </motion.div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 pt-24 sm:pt-32 pb-24 sm:pb-32 flex flex-col justify-end min-h-[80vh]">
        <motion.div
          {...fadeUpAnimated}
          transition={{ delay: 0.35, duration: 0.9, ease: "easeOut" }}
        >
          <div className="eyebrow mb-6">Authentic · West African · Omaha</div>
          <h1
            className="font-display uppercase leading-[0.85] tracking-tighter"
            style={{ fontSize: "clamp(3.5rem, 11vw, 11rem)", fontWeight: 500 }}
            data-testid="hero-title"
          >
            <span style={{ color: "var(--awd-text)" }}>Afro</span>{" "}
            <span style={{ color: "var(--awd-ochre)", fontStyle: "italic" }}>World</span>
            <br />
            <span style={{ color: "var(--awd-text)" }}>Delight</span>
          </h1>
          <p className="font-display italic text-2xl sm:text-3xl mt-6 max-w-2xl" style={{ color: "var(--awd-text-2)" }}>
            Bold flavors. Made fresh. Pre-ordered with love.
          </p>
          <p className="font-display italic text-2xl sm:text-3xl mt-2 max-w-2xl" style={{ color: "var(--awd-text-2)" }} data-testid="hero-tagline-2">
            Savor the world, celebrate the culture.
          </p>
        </motion.div>

        <motion.div
          {...fadeUpAnimated}
          transition={{ delay: 0.6, duration: 0.9 }}
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl"
        >
          <InfoCell icon={<Calendar size={18} />} label="Pickup Date" value="Saturday, Jun 6" />
          <InfoCell icon={<Clock size={18} />} label="Pickup Window" value="12 PM – 5 PM" />
          <InfoCell icon={<MapPin size={18} />} label="Location" value="Omaha, NE" />
        </motion.div>

        <motion.div
          {...fadeIn}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <button onClick={goMenu} className="btn-primary" data-testid="hero-view-menu-button">
            View Menu <ArrowDownRight size={16} />
          </button>
          <a href="#how" className="btn-ghost" data-testid="hero-how-to-order-button">How To Order</a>
        </motion.div>
      </div>
    </section>
  );
};

const InfoCell = ({ icon, label, value }) => (
  <div className="border-l pl-4 py-1" style={{ borderColor: "var(--awd-border-2)" }}>
    <div className="flex items-center gap-2 mb-1" style={{ color: "var(--awd-ochre)" }}>{icon}<span className="label-mini" style={{ color: "var(--awd-ochre)" }}>{label}</span></div>
    <div className="font-display text-xl" style={{ color: "var(--awd-text)" }}>{value}</div>
  </div>
);

export default Hero;
